<template>
  <div>
    <!-- AI要点速览 -->
    <div v-if="aiSummaryText || aiSummaryPoints.length || aiSummaryLoading" class="list-group-item">
      <div class="box box-left bac-white">
        <AIHeader :title="$t('ai_summary_title')" />

        <div style="padding: 5px 15px;">
          <!-- Loading State -->
          <div v-if="aiSummaryLoading && !aiSummaryText" class="ai-loading-container">
            <div class="ai-bubble">
              <div class="typing-indicator">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>

          <div v-if="aiSummaryText" style="font-size: 14px; color: #333; line-height: 1.3; margin: 15px 0; text-align: justify; white-space: pre-wrap;">{{ aiSummaryText }}</div>

          <div v-if="aiSummaryPoints && aiSummaryPoints.length" style="border-radius: 12px; padding: 0; font-size: 15px; color: #333; line-height: 1.8;">
            <div v-for="(point, pIndex) in aiSummaryPoints" :key="pIndex" style="margin-bottom: 2px;">
              <span style="font-weight: bold; color: #333;">• {{ point.label }}：</span>{{ point.content }}
            </div>
          </div>

          <div v-if="aiSummaryText" style="margin-top: 10px; display: flex; align-items: center; justify-content: flex-end;">
            <div v-if="isFromCache" style="margin-right: 15px; cursor: pointer; display: flex; align-items: center; color: #999;" @click="loadAiSummary(true)">
              <font-awesome-icon :icon="['fas', 'sync-alt']" style="font-size: 12px; margin-right: 4px;" />
              <span style="font-size: 12px;">{{ $t('refresh') }}</span>
            </div>
            <AISourceTag />
          </div>
        </div>
      </div>
    </div>

    <!-- AI下一步行动建议（NBA） -->
    <div v-if="nbaSuggestions.length" class="list-group-item">
      <div class="box box-left bac-white">
        <AIHeader :title="$t('ai_nba_suggestions_title')" />

        <div class="nba-suggestions-container">
          <div
            v-for="(suggestion, index) in nbaSuggestions"
            :key="getNbaSuggestionKey(suggestion, index)"
            class="nba-suggestion-item"
          >
            <div class="nba-suggestion-header">
              <span class="q-tag">{{ index + 1 }}</span>
              <div class="nba-suggestion-main">
                <span v-if="suggestion.suggestion_type" class="nba-action-type" :class="getNbaActionTypeClass(suggestion.suggestion_type)">
                  <font-awesome-icon :icon="getNbaActionTypeIcon(suggestion.suggestion_type)" class="nba-action-icon" />
                  <span>{{ getNbaActionTypeLabel(suggestion.suggestion_type) }}</span>
                </span>
                <span class="nba-suggestion-title">{{ getNbaSuggestionTitle(suggestion) | displayProductText }}</span>
              </div>
            </div>
            <div v-if="hasNbaMetaContext(suggestion)" class="nba-context-tags">
              <span v-if="suggestion.product_name" class="nba-context-tag is-product">
                <span class="nba-context-label">{{ $t('ai_nba_product_label') }}</span>
                <span>{{ suggestion.product_name | displayProductName }}</span>
              </span>
              <span v-if="suggestion.is_key_customer" class="nba-context-tag is-customer">{{ getCustomerLevelLabel(suggestion) }}</span>
              <span v-if="suggestion.physician_segment" class="nba-context-tag">{{ suggestion.physician_segment }}</span>
              <span v-if="getProductSalesSignalLabel(suggestion.product_sales_signal)" class="nba-context-tag is-signal">{{ getProductSalesSignalLabel(suggestion.product_sales_signal) }}</span>
            </div>
            <div v-if="getNbaTriggerTypeLabel(suggestion.trigger_type) || (suggestion.suggestion_reason && suggestion.suggestion_reason !== getNbaSuggestionTitle(suggestion))" class="nba-suggestion-line">
              <div class="nba-reason-heading">
                <span class="nba-suggestion-label">{{ $t('ai_nba_reason_label') }}</span>
                <span v-if="getNbaTriggerTypeLabel(suggestion.trigger_type)" class="nba-reason-trigger-tag">{{ getNbaTriggerTypeLabel(suggestion.trigger_type) }}</span>
              </div>
              <span v-if="suggestion.suggestion_reason && suggestion.suggestion_reason !== getNbaSuggestionTitle(suggestion)" class="nba-reason-text">{{ suggestion.suggestion_reason | displayProductText }}</span>
            </div>
            <div v-if="suggestion.suggestion_reason_detail && suggestion.suggestion_reason_detail !== suggestion.suggestion_reason && suggestion.suggestion_reason_detail !== getNbaSuggestionTitle(suggestion)" class="nba-suggestion-line">
              <span class="nba-suggestion-label">{{ $t('ai_nba_detail_label') }}</span>
              <span>{{ suggestion.suggestion_reason_detail | displayProductText }}</span>
            </div>
            <div v-if="suggestion.next_step && suggestion.next_step !== suggestion.suggestion_reason_detail && suggestion.next_step !== getNbaSuggestionTitle(suggestion)" class="nba-suggestion-line">
              <span class="nba-suggestion-label">{{ $t('ai_nba_next_step_label') }}</span>
              <span>{{ suggestion.next_step | displayProductText }}</span>
            </div>
            <div class="nba-action-row">
              <button
                type="button"
                class="accept-plan-btn"
                :class="getPrimaryActionClass(suggestion)"
                @click="handlePrimarySuggestionAction(suggestion)"
              >
                <font-awesome-icon :icon="getPrimaryActionIcon(suggestion)" class="accept-plan-btn-icon" />
                <span>{{ getPrimaryActionLabel(suggestion) }}</span>
              </button>
            </div>
          </div>

          <div class="nba-source-row">
            <AISourceTag />
          </div>
        </div>
      </div>
    </div>

    <!-- 观念反馈 -->
    <div class="list-group-item">
      <div class="box box-left bac-white">
        <AIHeader :title="$t('ai_interaction_summary')" />

        <div style="padding: 0 10px;">
          <div v-if="aiFeedbackLoading && !aiFeedbackData.length" class="ai-loading-container">
            <div class="ai-bubble">
              <div class="typing-indicator">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </div>
            </div>
          </div>

          <div v-if="aiFeedbackData.length || aiFollowUpQuestions.length" class="ai-feedback-container">
            <div v-if="!aiFeedbackLoading && aiFeedbackData.length === 0" style="padding: 10px 0; text-align: center; color: #999; font-size: 14px;">
              <div style="margin: 20px 0 0;">{{ $t('no_feedback_summary') }}</div>
            </div>
            <div v-for="(group, gIndex) in aiFeedbackData" :key="gIndex" class="feedback-group">
              <div class="feedback-group-header">
                <span class="group-title">{{ group.theme }}</span>
              </div>
              <div class="group-items">
                <div v-for="(item, index) in group.items" :key="index" class="feedback-item">
                  <div class="item-question">
                    <span class="q-tag">Q</span>
                    {{ item.question }}
                  </div>
                  <div class="item-summary">
                    <span class="q-tag">A</span>
                    {{ item.summary }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Follow up questions -->
            <div v-if="aiFollowUpQuestions.length" class="follow-up-section">
              <div class="follow-up-header"><font-awesome-icon :icon="['far', 'lightbulb']" class="hint-icon" /> {{ $t('further_inquiry_suggestions') }}</div>
              <div class="follow-up-list">
                <div v-for="(q, qIndex) in aiFollowUpQuestions" :key="qIndex" class="follow-up-btn" @click="askFollowUp(q)">
                  {{ q }}
                </div>
                <!-- 自定义提问 -->
                <div class="custom-input-wrapper">
                  <span ref="textMeasurer" class="text-measurer">{{ customQuestion || $t('input_further_question') }}</span>
                  <input
                    v-model="customQuestion"
                    class="custom-question-input"
                    :style="{ width: inputWidth + 'px' }"
                    :placeholder="$t('input_further_question')"
                    @keyup.enter="handleCustomQuestion"
                  />
                  <div v-if="customQuestion" class="send-btn" @click="handleCustomQuestion">
                    <font-awesome-icon :icon="['fas', 'paper-plane']" />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="aiFeedbackData.length || aiFollowUpQuestions.length" style="margin-top: 5px; display: flex; justify-content: flex-end;">
              <div v-if="isFeedbackFromCache" style="margin-right: 15px; cursor: pointer; display: flex; align-items: center; color: #999;" @click="loadAiFeedbackSummary(true)">
                <font-awesome-icon :icon="['fas', 'sync-alt']" style="font-size: 12px; margin-right: 4px;" />
                <span style="font-size: 12px;">{{ $t('refresh') }}</span>
              </div>
              <AISourceTag />
            </div>
          </div>

          <div v-if="!aiFeedbackLoading && aiFeedbackData.length === 0 && aiFollowUpQuestions.length === 0" style="padding: 20px; text-align: center; color: #999; font-size: 14px;">
            <div style="margin: 20px;">{{ $t('no_feedback_summary') }}</div>
            <div style="margin-top: 5px; display: flex; justify-content: flex-end;">
              <div v-if="isFeedbackFromCache" style="margin-right: 5px; cursor: pointer; display: flex; align-items: center; color: #999;" @click="loadAiFeedbackSummary(true)">
                <font-awesome-icon :icon="['fas', 'sync-alt']" style="font-size: 12px; margin-right: 4px;" />
                <span style="font-size: 12px;">{{ $t('refresh') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import _ from 'underscore';
// Use standard imports from existing file
import { getPhysicianAiSummary, streamHCPSummary, savePhysicianAiSummary, getPhysicianAiFeedbackSummary, savePhysicianAiFeedbackSummary, streamHCPFeedback } from '@/api/ai';
import { getNbaSuggestions, saveNbaSuggestionFeedback } from '@/api/Physicians';
import { getContentById } from '@/api/content';
import { getLocalStorage, isGBA } from '@/utils/index';
import * as moment from 'moment';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import AISourceTag from '@/components/AISourceTag.vue';
import AIHeader from '@/components/AIHeader.vue';
import { displayProductName } from '@/utils/productDisplay';
import {
  getCanonicalNbaActionType,
  getNbaActionTypeClass,
  getNbaActionTypeI18nKey,
  getNbaActionTypeIcon,
  getNbaTriggerTypeI18nKey,
  normalizeNbaEnumValue
} from '@/utils/nbaSuggestionEnums';

const SAVED_AI_CHARS_PER_FRAME = 4;

export default {
  name: 'AIAnalysis',
  components: {
    AISourceTag,
    AIHeader,
  },
  props: {
    physician_id: {
      type: [String, Number],
      required: true
    },
    hcpData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      aiSummaryText: '',
      aiSummaryPoints: [],
      aiSummaryLoading: false,
      isFromCache: false,

      aiFeedbackLoading: false,
      isFeedbackFromCache: false,
      aiFeedbackRawText: '',
      aiFeedbackData: [],
      aiFollowUpQuestions: [],
      customQuestion: '',
      inputWidth: 128,

      nbaSuggestions: [],
      nbaSuggestionsAnimationFrame: null,
      nbaSuggestionsAnimationRunId: 0,
      acceptSuggestionIcon: faCircleCheck,
      contentDetailCache: {},
    };
  },
  computed: {
    aiLanguage() {
      const language = typeof window !== 'undefined' ? window.localStorage.getItem('language') : '';
      return /^en/i.test(language) ? 'en-US' : 'zh-CN';
    },
    summaryTitlePattern() {
      return /【\s*(AI\s*要点速览|AI\s*Summary|AI\s*Key\s*(?:Highlights?|Insights?))\s*】\s*/gi;
    },
    summaryOneLinePattern() {
      return /(^|[\r\n]+)\s*(一句话总结|One[-\s]*(?:line|sentence) Summary)[：:]\s*/gi;
    },
    followUpSectionPattern() {
      return /[*#]*【\s*(进一步追问参考|Further Follow-up Suggestions)\s*】[*#]*/;
    },
    feedbackSummaryTitlePattern() {
      return /【\s*(拜访反馈总结|Visit Feedback Summary|Call Feedback Summary)\s*】/i;
    },
  },
  watch: {
    customQuestion() {
      this.updateInputWidth();
    }
  },
  mounted() {
    this.loadAiSummary();
    this.loadNbaSuggestions();
    this.loadAiFeedbackSummary();
    this.updateInputWidth();
  },
  beforeDestroy() {
    this.cancelNbaSuggestionsAnimation();
  },
  methods: {
    async loadNbaSuggestions() {
      if (!this.physician_id) return;

      try {
        const apiRes = await getNbaSuggestions({ physician_id: this.physician_id });
        if (apiRes && apiRes.isSuccess === false) {
          throw new Error(apiRes.message || 'GetNbaSuggestions failed');
        }
        this.applyNbaSuggestions(this.getApiDataList(apiRes));
      } catch (e) {
        console.error('Fetch NBA suggestions failed', e);
        this.nbaSuggestions = [];
      }
    },
    applyNbaSuggestions(apiList) {
      const sortedSuggestions = this.sortNbaSuggestions(
        apiList
          .filter(this.isCurrentPhysicianSuggestion)
          .filter(this.isPendingNbaSuggestion)
          .map(item => this.mapNbaSuggestionItem(item))
          .filter(item => this.hasNbaSuggestionContent(item))
      );
      this.animateNbaSuggestions(sortedSuggestions);
    },
    cancelNbaSuggestionsAnimation() {
      this.nbaSuggestionsAnimationRunId += 1;
      if (this.nbaSuggestionsAnimationFrame) {
        cancelAnimationFrame(this.nbaSuggestionsAnimationFrame);
        this.nbaSuggestionsAnimationFrame = null;
      }
    },
    animateNbaSuggestions(list) {
      this.cancelNbaSuggestionsAnimation();
      this.nbaSuggestions = [];

      if (!list.length) return;

      const runId = this.nbaSuggestionsAnimationRunId;
      const fields = ['suggestion_title', 'suggestion_reason', 'suggestion_reason_detail', 'next_step'];
      const step = SAVED_AI_CHARS_PER_FRAME;
      let itemIndex = 0;
      let fieldIndex = 0;
      let charIndex = 0;

      const revealNext = () => {
        if (runId !== this.nbaSuggestionsAnimationRunId) return;

        if (itemIndex >= list.length) {
          this.nbaSuggestionsAnimationFrame = null;
          return;
        }

        if (!this.nbaSuggestions[itemIndex]) {
          const source = list[itemIndex];
          this.nbaSuggestions.push({
            ...source,
            suggestion_title: '',
            suggestion_reason: '',
            suggestion_reason_detail: '',
            next_step: ''
          });
        }

        const source = list[itemIndex];
        const displayItem = this.nbaSuggestions[itemIndex];
        const field = fields[fieldIndex];
        const fullText = String(source[field] || '');

        if (!fullText) {
          fieldIndex += 1;
          charIndex = 0;
        } else if (charIndex < fullText.length) {
          charIndex += step;
          displayItem[field] = fullText.slice(0, charIndex);
        } else {
          fieldIndex += 1;
          charIndex = 0;
        }

        if (fieldIndex >= fields.length) {
          itemIndex += 1;
          fieldIndex = 0;
          charIndex = 0;
        }

        this.nbaSuggestionsAnimationFrame = requestAnimationFrame(revealNext);
      };

      this.nbaSuggestionsAnimationFrame = requestAnimationFrame(revealNext);
    },
    getApiDataList(res) {
      if (!res) return [];
      if (Array.isArray(res)) return res;
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.value)) return res.value;
      return [];
    },
    isCurrentPhysicianSuggestion(item) {
      if (!item) return false;
      const detail = this.parseSuggestionContent(item.suggestion_content);
      const id = item.physician_id || detail.physician_id || item.physicianId || detail.physicianId || item.id || detail.id;
      return String(id) === String(this.physician_id);
    },
    isPendingNbaSuggestion(item) {
      return !item || !(item.feedback_status || item.feedbackStatus);
    },
    parseSuggestionContent(content) {
      if (!content) return {};
      if (typeof content === 'object') return content;
      try {
        return JSON.parse(content);
      } catch (e) {
        return {};
      }
    },
    mapNbaSuggestionItem(item) {
      const detail = this.parseSuggestionContent(item.suggestion_content);
      const actionType = getCanonicalNbaActionType(item.suggestion_type || detail.action_type || '');
      const reason = item.suggestion_reason_short || detail.suggestion_reason || item.suggestion_reason || detail.suggestion_detail || detail.recommended_action || '';
      const detailText = item.suggestion_reason || detail.suggestion_detail || detail.next_step || '';
      const title = detail.recommended_action || item.suggestion_reason_short || detail.next_step || reason || '';

      const mapped = {
        ...item,
        ...detail,
        physician_id: item.physician_id || detail.physician_id || item.physicianId || detail.physicianId,
        suggestion_type: actionType,
        suggestion_title: title,
        suggestion_reason: reason,
        suggestion_reason_detail: detailText,
        next_step: detail.next_step || '',
        recommended_topic: item.recommended_topic || detail.recommended_topic || '',
        recommended_content_id: item.recommended_content_id || detail.recommended_content_id || '',
        recommended_content_title: item.recommended_content_title || detail.recommended_content_title || '',
        recommended_action: item.recommended_action || detail.recommended_action || '',
        physician_name: item.physician_name || detail.physician_name || '',
        institution_name: item.institution_name || detail.institution_name || '',
        customer_level: this.getCustomerLevel(item, detail),
        is_key_customer: this.isKeyCustomer(item, detail),
        product_id: item.product_id || detail.product_id || '',
        product_name: item.product_name || detail.product_name || '',
        physician_segment: item.physician_segment || detail.physician_segment || '',
        product_sales_periods: item.product_sales_periods || detail.product_sales_periods || [],
        product_sales_signal: normalizeNbaEnumValue(item.product_sales_signal || detail.product_sales_signal),
        priority: normalizeNbaEnumValue(item.priority || detail.priority),
        trigger_type: normalizeNbaEnumValue(item.trigger_type || detail.trigger_type),
        feedback_status: item.feedback_status || detail.feedback_status || item.feedbackStatus || detail.feedbackStatus || '',
      };
      return this.sanitizeNbaSuggestionPayload(mapped);
    },
    hasNbaSuggestionContent(item) {
      return Boolean(item && (
        item.suggestion_title ||
        item.suggestion_reason ||
        item.suggestion_reason_detail ||
        item.next_step
      ));
    },
    sortNbaSuggestions(list) {
      const priorityRank = {
        high: 0,
        medium: 1,
        low: 2
      };
      return list.sort((a, b) => {
        const priorityDiff = (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99);
        if (priorityDiff !== 0) return priorityDiff;
        return (Number(b.recommendation_score) || 0) - (Number(a.recommendation_score) || 0);
      });
    },
    getNbaSuggestionKey(suggestion, index) {
      if (!suggestion) return `ai-nba-suggestion-${index}`;
      return suggestion.nba_suggestion_id || suggestion.action_id || `${suggestion.physician_id || 'unknown'}-${suggestion.product_id || 'no-product'}-${suggestion.suggestion_type || index}`;
    },
    getNbaSuggestionTitle(suggestion) {
      return suggestion && suggestion.suggestion_title ? suggestion.suggestion_title : '';
    },
    getNbaActionTypeLabel(type) {
      return this.$t(getNbaActionTypeI18nKey(type));
    },
    getNbaActionTypeIcon(type) {
      return getNbaActionTypeIcon(type);
    },
    getNbaActionTypeClass(type) {
      return getNbaActionTypeClass(type);
    },
    getNbaTriggerTypeLabel(triggerType) {
      const i18nKey = getNbaTriggerTypeI18nKey(triggerType);
      return i18nKey ? this.$t(i18nKey) : '';
    },
    getCustomerLevel(item = {}, detail = {}) {
      return item.customer_level || detail.customer_level ||
        item.hcp_level || detail.hcp_level ||
        item.physician_level || detail.physician_level ||
        item.hcp_grade || detail.hcp_grade ||
        item.customer_grade || detail.customer_grade ||
        '';
    },
    isKeyCustomer(item = {}, detail = {}) {
      if (typeof item.is_key_customer === 'boolean') return item.is_key_customer;
      if (typeof detail.is_key_customer === 'boolean') return detail.is_key_customer;
      return ['A', 'B'].includes(String(this.getCustomerLevel(item, detail) || '').trim().toUpperCase());
    },
    hasNbaMetaContext(suggestion) {
      return Boolean(suggestion && (
        suggestion.product_name ||
        suggestion.is_key_customer ||
        suggestion.physician_segment ||
        this.getProductSalesSignalLabel(suggestion.product_sales_signal)
      ));
    },
    getCustomerLevelLabel(suggestion) {
      const level = String(suggestion && suggestion.customer_level || '').trim().toUpperCase();
      if (!level) return this.$t('ai_nba_key_customer');
      return this.$t('ai_nba_customer_level', { level });
    },
    getProductSalesSignalLabel(signal) {
      const keyMap = {
        continuous_decline: 'ai_nba_product_sales_signal_continuous_decline',
        sales_drop: 'ai_nba_product_sales_signal_sales_drop',
        target_gap: 'ai_nba_product_sales_signal_target_gap',
        abnormal_fluctuation: 'ai_nba_product_sales_signal_abnormal_fluctuation'
      };
      const i18nKey = keyMap[normalizeNbaEnumValue(signal)];
      return i18nKey ? this.$t(i18nKey) : '';
    },
    sanitizeNbaSuggestionPayload(suggestion = {}) {
      const sanitized = { ...suggestion };
      if (!this.hasSensitiveSalesText(sanitized)) return sanitized;

      const safeText = this.getSafeProductTrendText(sanitized);
      [
        'suggestion_title',
        'suggestion_reason',
        'suggestion_reason_detail',
        'suggestion_detail',
        'recommended_action',
        'next_step'
      ].forEach((field) => {
        if (sanitized[field] && this.containsSalesSensitiveTerms(sanitized[field])) {
          sanitized[field] = safeText;
        }
      });

      if (Array.isArray(sanitized.trigger_evidence)) {
        sanitized.trigger_evidence = sanitized.trigger_evidence.map(evidence =>
          this.containsSalesSensitiveTerms(evidence) ? safeText : evidence
        );
      }

      return sanitized;
    },
    hasSensitiveSalesText(suggestion = {}) {
      const triggerType = normalizeNbaEnumValue(suggestion.trigger_type);
      const sourceAgent = normalizeNbaEnumValue(suggestion.source_agent);
      const sourceRule = normalizeNbaEnumValue(suggestion.source_rule);
      const salesSignal = normalizeNbaEnumValue(suggestion.product_sales_signal);
      const isProductTrendSuggestion = triggerType === 'product_sales_trend' ||
        sourceAgent === 'nba_sales_trend_agent' ||
        sourceRule.indexOf('product_sales') >= 0 ||
        sourceRule.indexOf('product_target') >= 0 ||
        Boolean(salesSignal && salesSignal !== 'no_signal' && salesSignal !== 'null');

      const textFields = [
        suggestion.suggestion_title,
        suggestion.suggestion_reason,
        suggestion.suggestion_reason_detail,
        suggestion.suggestion_detail,
        suggestion.recommended_action,
        suggestion.next_step,
        ...(Array.isArray(suggestion.trigger_evidence) ? suggestion.trigger_evidence : [])
      ];

      return isProductTrendSuggestion && textFields.some(text => this.containsSalesSensitiveTerms(text));
    },
    containsSalesSensitiveTerms(value) {
      const text = String(value || '');
      return /(销量|销售额|销售|目标|达成|低于|处方|推广|冲量|%|％|\d+\s*月|\d+(?:\.\d+)?\s*(?:%|％)|sales|target|achievement|prescription)/i.test(text);
    },
    getSafeProductTrendText(suggestion = {}) {
      const institution = suggestion.institution_name || suggestion.hospital || '';
      const product = displayProductName(suggestion.product_name || '', suggestion.product_id || '');
      if (/^en/i.test(this.$i18n.locale)) {
        return `${institution ? `${institution} ` : ''}${product ? `${product} ` : ''}recent in-hospital product trend signals suggest follow-up on access, inventory, and compliant scientific communication opportunities.`;
      }
      return `${institution}${product}近期院内产品趋势出现波动，建议了解院内准入、库存及合规学术沟通机会。`;
    },
    isContentPushSuggestion(suggestion) {
      return getCanonicalNbaActionType(suggestion && suggestion.suggestion_type) === 'content_push';
    },
    getContentPushTopic(suggestion) {
      return suggestion && (suggestion.recommended_topic || suggestion.topic || '');
    },
    getContentPushContentId(suggestion) {
      return suggestion && (suggestion.recommended_content_id || suggestion.content_id || '');
    },
    getContentPushContentTitle(suggestion) {
      return suggestion && (suggestion.recommended_content_title || suggestion.content_title || '');
    },
    getPrimaryActionLabel(suggestion) {
      return this.isContentPushSuggestion(suggestion) ? this.$t('send_content') : this.$t('create_visit_plan');
    },
    getPrimaryActionIcon(suggestion) {
      return this.isContentPushSuggestion(suggestion) ? ['fas', 'paper-plane'] : this.acceptSuggestionIcon;
    },
    getPrimaryActionClass(suggestion) {
      return {
        'is-content-push': this.isContentPushSuggestion(suggestion)
      };
    },
    async handlePrimarySuggestionAction(suggestion) {
      if (this.isContentPushSuggestion(suggestion)) {
        await this.handleSendContentSuggestion(suggestion);
        return;
      }
      await this.handleAcceptSuggestion(suggestion);
    },
    async handleAcceptSuggestion(suggestion) {
      if (!suggestion || !suggestion.physician_id) return;

      await this.submitNbaFeedback(suggestion, 'accepted', {
        feedback_comment: 'create_visit_plan'
      });
      this.goToCreateVisit(suggestion);
    },
    async handleSendContentSuggestion(suggestion) {
      if (!suggestion || !suggestion.physician_id) return;

      const contentId = this.getContentPushContentId(suggestion);
      if (!contentId) {
        this.goToSendContent({
          ...suggestion,
          recommended_topic: this.getContentPushTopic(suggestion),
          recommended_content_id: '',
          recommended_content_title: this.getContentPushContentTitle(suggestion)
        });
        return;
      }

      const content = await this.loadContentDetail(contentId);
      if (!content) {
        this.$toast.fail(this.$t('get_content_failed'));
        return;
      }

      await this.forwardContentSuggestion(suggestion);
    },
    async loadContentDetail(contentId) {
      if (!contentId) return null;
      if (this.contentDetailCache[contentId]) {
        return this.contentDetailCache[contentId];
      }

      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });
      toast.show();

      try {
        const res = await getContentById(contentId);
        const list = res && Array.isArray(res.value)
          ? res.value
          : (res && Array.isArray(res.data) ? res.data : []);
        const content = list[0] || null;
        if (content) {
          this.$set(this.contentDetailCache, contentId, content);
        }
        return content;
      } catch (e) {
        console.error('Failed to load NBA content detail:', e);
        return null;
      } finally {
        toast.hide();
      }
    },
    async forwardContentSuggestion(suggestion) {
      const toast = this.$createToast({
        time: 10000,
        mask: false,
      });

      try {
        toast.show();
        const saved = await this.submitNbaFeedback(suggestion, 'accepted', {
          feedback_comment: 'send_content'
        });
        if (saved) {
          this.removeNbaSuggestion(suggestion);
        }
        this.showDemoContentPushSuccess();
      } catch (err) {
        console.error('Forward NBA content error:', err);
        this.$toast.fail(this.$t('forward_failed'));
      } finally {
        toast.hide();
      }
    },
    showDemoContentPushSuccess() {
      const message = this.$t('demo_content_pushed_to_customer_wechat');
      if (this.$toast && this.$toast.success) {
        this.$toast.success(message);
        return;
      }
      this.$toast(message);
    },
    async submitNbaFeedback(suggestion, feedbackStatus, extra = {}) {
      if (!suggestion || !suggestion.nba_suggestion_id) {
        console.warn('Skip NBA feedback: missing nba_suggestion_id', suggestion);
        return false;
      }

      const userContext = this.getUserContext();

      try {
        const res = await saveNbaSuggestionFeedback({
          nba_suggestion_id: suggestion.nba_suggestion_id,
          feedback_status: feedbackStatus,
          reject_reason: extra.reject_reason || '',
          feedback_comment: extra.feedback_comment || '',
          user_id: userContext.user_id,
          territory_id: userContext.territory_id,
          territory_type: userContext.territory_type,
          role_id: userContext.role_id
        });
        if (res && res.isSuccess === false) {
          throw new Error(res.message || 'NbaSuggestionFeedback failed');
        }
        return true;
      } catch (e) {
        console.error('Save NBA feedback failed', e);
        return false;
      }
    },
    removeNbaSuggestion(suggestion) {
      if (!suggestion) return;

      const targetKey = this.getNbaSuggestionKey(suggestion);
      this.nbaSuggestions = this.nbaSuggestions.filter(item => this.getNbaSuggestionKey(item) !== targetKey);
    },
    getUserContext() {
      const userInfo = JSON.parse(getLocalStorage('userInfo') || '{}');
      return {
        user_id: getLocalStorage('user_id') || userInfo.userId || userInfo.id || '',
        territory_id: getLocalStorage('territory_id') || userInfo.territoryId || '',
        territory_type: getLocalStorage('territory_type') || userInfo.territoryType || '',
        role_id: getLocalStorage('role_id') || userInfo.roleId || ''
      };
    },
    goToCreateVisit(suggestion) {
      const today = moment().format('YYYY/MM/DD');
      if (isGBA()) {
        this.$router.push({
          path: '/gba/visit-create',
          query: {
            physician_id: suggestion.physician_id,
            product_id: suggestion.product_id || '',
            product_name: suggestion.product_name || '',
            visit_date: today,
            visit_time: '上午',
            visit_type: '面对面拜访',
            visit_goal: 'NRDL'
          }
        });
        return;
      }

      this.$router.push({
        path: '/hcp/visit-create',
        query: {
          physician_id: suggestion.physician_id,
          product_id: suggestion.product_id || '',
          product_name: suggestion.product_name || '',
          visit_date: today,
          visit_time: '上午',
          visit_type: '面对面拜访',
          visit_goal: '产品信息传递'
        }
      });
    },
    goToSendContent(suggestion) {
      const path = isGBA() ? '/gba/content' : '/hcp/content';
      this.$router.push({
        path,
        query: {
          physician_id: suggestion.physician_id || '',
          physician_name: suggestion.physician_name || '',
          nba_suggestion_id: suggestion.nba_suggestion_id || '',
          recommended_topic: suggestion.recommended_topic || '',
          recommended_content_id: suggestion.recommended_content_id || '',
          recommended_content_title: suggestion.recommended_content_title || '',
          product_id: suggestion.product_id || '',
          product_name: suggestion.product_name || ''
        }
      });
    },
    updateInputWidth() {
      this.$nextTick(() => {
        if (this.$refs.textMeasurer) {
          // padding 40 + border 2 + buffer 5
          this.inputWidth = this.$refs.textMeasurer.offsetWidth + 47;
        }
      });
    },
    async loadAiSummary(forceRefresh = false) {
      if (!this.physician_id) return;
      this.aiSummaryLoading = true;

      // 1. Try Cache if not forced
      if (!forceRefresh) {
        try {
          const res = await getPhysicianAiSummary(this.physician_id);
          if (res && res.value && res.value.length > 0) {
            const summaryData = res.value[0];
            const lastTime = moment(summaryData.summary_time);
            if (moment().diff(lastTime, 'days') < 3) {
              const rawSummary = summaryData.summary || summaryData.content;
              if (rawSummary) {
                // Clean/Parse Logic
                let parsed = false;
                try {
                  let cleanJson = rawSummary.replace(/^```json\s*/, '').replace(/\s*```$/, '');
                  const contentObj = JSON.parse(cleanJson);
                  if (contentObj.summary || contentObj.points) {
                    this.aiSummaryText = contentObj.summary || contentObj.text || '';
                    this.aiSummaryPoints = contentObj.points || [];
                    parsed = true;
                  }
                } catch (e) {
                // Not JSON, will handle as plain text
                }

                if (!parsed) {
                  let val = rawSummary.replace(this.summaryTitlePattern, '');
                  val = val.replace(this.summaryOneLinePattern, '$1');
                  val = val.trim();

                  // Simulate streaming for cache
                  this.aiSummaryText = '';
                  this.isFromCache = true;
                  this.aiSummaryLoading = true;

                  let index = 0;
                  const step = SAVED_AI_CHARS_PER_FRAME;
                  const animate = () => {
                    if (index < val.length) {
                      this.aiSummaryText += val.slice(index, index + step);
                      index += step;
                      requestAnimationFrame(animate);
                    } else {
                      this.aiSummaryLoading = false;
                    }
                  };
                  requestAnimationFrame(animate);
                } else {
                  this.aiSummaryLoading = false;
                  this.isFromCache = true;
                }
                return;
              }
            }
          }
        } catch (e) {
          console.error('OData fetch failed', e);
        }
      }

      this.aiSummaryText = '';
      this.aiSummaryPoints = [];
      this.isFromCache = false;

      // 2. Stream
      let buffer = '';
      let accumulatedText = '';

      try {
        await streamHCPSummary({
          data: {
            content: `physician_id=${this.physician_id}&today=${moment().format('YYYY-MM-DD')}`,
            language: this.aiLanguage,
          },
          onChunk: (chunk) => {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.substring(5).trim();
              if (!jsonStr) continue;

              try {
                const json = JSON.parse(jsonStr);
                if (json.output && typeof json.output.text === 'string') {
                  const newText = json.output.text;
                  // Bailian incremental check
                  if (accumulatedText && newText.startsWith(accumulatedText)) {
                    accumulatedText = newText;
                  } else {
                    accumulatedText += newText;
                  }
                  // Clean up specific prefixes
                  let val = accumulatedText.replace(this.summaryTitlePattern, '');
                  val = val.replace(this.summaryOneLinePattern, '$1');
                  val = val.trim();
                  this.aiSummaryText = val;
                }
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
          },
          onComplete: () => {
            this.aiSummaryLoading = false;

            // Save to OData
            if (accumulatedText) {
              savePhysicianAiSummary({
                physician_id: this.physician_id,
                summary: accumulatedText,
                summary_time: moment().format('YYYY-MM-DD HH:mm:ss')
              }).catch(e => console.error('Save summary failed', e));
            }

            // Try to parse the accumulated text as JSON to extract points
            try {
              // If the stream output was a JSON string
              // Clean markdown code blocks if present
              let cleanJson = accumulatedText.replace(/^```json\s*/, '').replace(/\s*```$/, '');
              const contentObj = JSON.parse(cleanJson);
              if (contentObj.summary || contentObj.points) {
                this.aiSummaryText = contentObj.summary || contentObj.text || '';
                this.aiSummaryPoints = contentObj.points || [];
              }
            } catch (e) {
              // Not JSON, just keep the text
            }
          },
          onError: () => {
            this.aiSummaryLoading = false;
          }
        });
      } catch (e) {
        this.aiSummaryLoading = false;
      }
    },
    parseFeedbackText(text) {
      if (!text) return { groups: [], followUps: [] };

      // Split main content and follow-ups. Support optional markdown bold/heading chars
      const parts = text.split(this.followUpSectionPattern);
      const mainText = parts[0];
      const followUpText = parts.length > 1 ? parts[1] : '';

      const lines = mainText.split('\n');
      const result = [];
      let currentGroup = null;
      let currentItem = null;

      for (let line of lines) {
        line = line.trim();
        if (!line) continue;
        if (this.feedbackSummaryTitlePattern.test(line)) continue;

        // Check if it's a question line
        if (/^Q[：:]/.test(line)) {
          if (!currentGroup) {
            // Fallback if no theme yet
            currentGroup = { theme: this.$t('other'), icon: 'comment-dots', type: 'info', items: [] };
            result.push(currentGroup);
          }
          currentItem = { question: line.replace(/^Q[：:]\s*/, ''), summary: '' };
          currentGroup.items.push(currentItem);
        } else if (/^A[：:]/.test(line)) {
          if (currentItem) {
            currentItem.summary = line.replace(/^A[：:]\s*/, '');
          }
        } else {
          // Clean brackets if any, e.g. <Theme>
          const themeName = line.replace(/[<>]/g, '');
          currentGroup = { theme: themeName, items: [] };

          // Default style
          currentGroup.icon = 'tasks';
          currentGroup.type = 'process';

          // Simple keyword matching for icon/color
          if (themeName.includes(this.$t('risk')) || themeName.includes(this.$t('barrier')) || themeName.includes(this.$t('difficulty'))) {
            currentGroup.icon = 'minus-circle';
            currentGroup.type = 'warning';
          } else if (themeName.includes(this.$t('opportunity')) || themeName.includes(this.$t('advantage'))) {
            currentGroup.icon = 'lightbulb';
            currentGroup.type = 'success'; // You might need to define 'success' color css
          }

          result.push(currentGroup);
          currentItem = null;
        }
      }

      // Post-processing to filter out empty feedbacks
      const groups = result.map(group => {
        group.items = group.items.filter(item => {
          const q = item.question || '';
          const a = item.summary || '';
          return !q.includes(this.$t('no_explicit_feedback')) && !q.includes(this.$t('no_feedback')) &&
                       !a.includes(this.$t('no_explicit_feedback')) && !a.includes(this.$t('no_feedback'));
        });
        return group;
      }).filter(group => group.items.length > 0);

      // Parse Follow Ups
      const followUps = [];
      if (followUpText) {
        const fLines = followUpText.split('\n');
        for (let line of fLines) {
          line = line.trim();
          if (!line) continue;
          // Match - or • or * or 1.
          if (line.startsWith('-') || line.startsWith('•') || line.startsWith('*')) {
            followUps.push(line.replace(/^[-•*]\s*/, ''));
          } else if (/^\d+\./.test(line)) {
            followUps.push(line.replace(/^\d+\.\s*/, ''));
          }
        }
      }

      return { groups, followUps };
    },
    async loadAiFeedbackSummary(forceRefresh = false) {
      if (!this.physician_id) return;
      this.aiFeedbackLoading = true;

      // 1. Try Cache
      if (!forceRefresh) {
        try {
          const res = await getPhysicianAiFeedbackSummary(this.physician_id);
          if (res && res.value && res.value.length > 0) {
            const summaryData = res.value[0];
            const lastTime = moment(summaryData.summary_time);
            if (moment().diff(lastTime, 'days') < 3) {
              const rawSummary = summaryData.summary || summaryData.content;
              if (rawSummary) {
                this.aiFeedbackRawText = rawSummary;
                // Parse logic
                // const parsedData = this.parseFeedbackText(rawSummary);

                // Simulate streaming for UI elements appearance (optional, maybe just show data)
                // Since it is complex structure, just show it.
                // this.aiFeedbackData = parsedData;

                // this.aiFeedbackLoading = false;
                this.isFeedbackFromCache = true;

                this.aiFeedbackLoading = true;
                this.aiFeedbackData = [];
                this.aiFollowUpQuestions = [];

                let index = 0;
                const step = SAVED_AI_CHARS_PER_FRAME;
                let currentText = '';

                const animate = () => {
                  if (index < rawSummary.length) {
                    currentText += rawSummary.slice(index, index + step);
                    index += step;

                    const parsed = this.parseFeedbackText(currentText);
                    this.aiFeedbackData = parsed.groups;
                    this.aiFollowUpQuestions = parsed.followUps;
                    requestAnimationFrame(animate);
                  } else {
                    this.aiFeedbackLoading = false;
                    // Ensure final consistent state
                    const parsed = this.parseFeedbackText(rawSummary);
                    this.aiFeedbackData = parsed.groups;
                    this.aiFollowUpQuestions = parsed.followUps;
                  }
                };
                requestAnimationFrame(animate);

                return;
              }
            }
          }
        } catch (e) {
          console.error('OData feedback fetch failed', e);
        }
      }

      this.aiFeedbackData = [];
      this.aiFollowUpQuestions = [];
      this.aiFeedbackRawText = '';
      this.isFeedbackFromCache = false;

      // 2. Stream
      let buffer = '';
      let accumulatedText = '';

      try {
        await streamHCPFeedback({
          data: {
            content: `physician_id=${this.physician_id}&today=${moment().format('YYYY-MM-DD')}`,
            language: this.aiLanguage,
          },
          onChunk: (chunk) => {
            buffer += chunk;
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const jsonStr = trimmed.substring(5).trim();
              if (!jsonStr) continue;

              try {
                const json = JSON.parse(jsonStr);
                if (json.output && typeof json.output.text === 'string') {
                  const newText = json.output.text;
                  // Incremental check
                  if (accumulatedText && newText.startsWith(accumulatedText)) {
                    accumulatedText = newText;
                  } else {
                    accumulatedText += newText;
                  }
                  // Try to parse accumulated text in real-time to update UI
                  const parsed = this.parseFeedbackText(accumulatedText);
                  this.aiFeedbackData = parsed.groups;
                  this.aiFollowUpQuestions = parsed.followUps;
                }
              } catch (e) {
                // Ignore JSON parse errors
              }
            }
          },
          onComplete: () => {
            this.aiFeedbackLoading = false;

            // Final parse
            if (accumulatedText) {
              const parsed = this.parseFeedbackText(accumulatedText);
              this.aiFeedbackData = parsed.groups;
              this.aiFollowUpQuestions = parsed.followUps;

              // Save to OData
              savePhysicianAiFeedbackSummary({
                physician_id: this.physician_id,
                summary: accumulatedText,
                summary_time: moment().format('YYYY-MM-DD HH:mm:ss')
              }).catch(e => console.error('Save feedback summary failed', e));
            }
          },
          onError: () => {
            this.aiFeedbackLoading = false;
          }
        });
      } catch (e) {
        this.aiFeedbackLoading = false;
      }
    },
    handleCustomQuestion() {
      if (!this.customQuestion.trim()) return;
      this.askFollowUp(this.customQuestion);
      this.customQuestion = '';
    },
    askFollowUp(question) {
      const pData = this.hcpData || {};
      let message = '';
      const name = pData.physician_name || pData.name;
      if (name && pData.institution_name) {
        message = this.$t('based_on_doctor_feedback', {
          institution: pData.institution_name,
          name: name,
          question: question
        });
      } else {
        message = this.$t('based_on_doctor', {
          question: question
        });
      }
      this.$root.$emit('open-ai-chat', message);
    },
  },
};
</script>

<style scoped lang="scss">
.nba-suggestions-container {
  padding: 5px 8px 12px;
  text-align: left;
}

.nba-suggestion-item {
  margin: 12px 0;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.nba-suggestion-item:first-child {
  margin-top: 10px;
}

.nba-suggestion-header {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 6px;
}

.nba-suggestion-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
  line-height: 1;
}

.nba-suggestion-title {
  display: block;
  font-size: 14px;
  color: #333;
  font-weight: 600;
  line-height: 1.5;
  margin-top: 4px;
}

.nba-context-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 4px 22px;
}

.nba-context-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  max-width: 100%;
  min-height: 20px;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f5f5f5;
  color: #666;
  font-size: 11px;
  line-height: 16px;
  box-sizing: border-box;
}

.nba-context-tag.is-product {
  background: #f2f7f4;
  color: #2f6f4e;
  font-weight: 600;
}

.nba-context-tag.is-customer {
  background: #f1fff7;
  color: #9a5f00;
  font-weight: 600;
}

.nba-context-tag.is-signal {
  background: #f1fff7;
  color: #00b852;
}

.nba-context-label {
  color: inherit;
  opacity: 0.68;
  font-weight: 400;
}

.nba-action-type {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  box-sizing: border-box;
  margin-top: 0;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f6f3f2;
  color: #7f3b2a;
  font-size: 11px;
  line-height: 16px;
}

.nba-action-icon {
  flex-shrink: 0;
  font-size: 10px;
}

.nba-suggestion-line {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin-top: 4px;
  white-space: pre-wrap;
}

.nba-reason-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.nba-reason-trigger-tag {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  max-width: 58%;
  padding: 1px 6px;
  border-radius: 10px;
  background: #fafafa;
  color: #00b852;
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nba-reason-text {
  display: block;
}

.nba-suggestion-label {
  color: #333;
  font-weight: 500;
}

.nba-source-row {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.nba-action-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.accept-plan-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 96px;
  height: 28px;
  padding: 0 12px;
  border: 1.5px solid #00b852;
  border-radius: 16px;
  background: transparent;
  color: #00b852;
  font-size: 12px;
  font-weight: 600;
}

.accept-plan-btn.is-content-push {
  border-color: #1f6fb5;
  color: #1f6fb5;
}

.accept-plan-btn:focus {
  outline: none;
}

.accept-plan-btn:hover {
  background: rgba(0, 184, 82, 0.04);
}

.accept-plan-btn:active {
  background: rgba(0, 184, 82, 0.08);
}

.accept-plan-btn.is-content-push:hover {
  background: rgba(31, 111, 181, 0.05);
}

.accept-plan-btn.is-content-push:active {
  background: rgba(31, 111, 181, 0.1);
}

.accept-plan-btn-icon {
  flex-shrink: 0;
  font-size: 12px;
}

.ai-feedback-container {
  padding: 0 0 15px 0;
  text-align: left;
}
.ai-hint {
  font-size: 12px;
  color: #999;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  background: #f9f9f9;
  padding: 5px 10px;
  border-radius: 4px;
}
.hint-icon {
  margin-right: 5px;
  font-size: 14px;
}
.feedback-group {
  margin-bottom: 20px;
}
.feedback-group:last-child {
  margin-bottom: 0;
}
.feedback-group-header {
  display: flex;
  align-items: center;
  margin-top: 15px;
  margin-bottom: 10px;
  padding-bottom: 5px;
}
.group-icon {
  font-size: 18px;
  margin-right: 8px;
}
.group-icon.warning {
  color: #ff976a;
}
.group-icon.process {
  color: #1989fa;
}
.group-icon.success {
  color: #07c160;
}
.group-title {
  font-size: 14px;
  font-style: italic;
  color: #666;
}
.feedback-item {
  margin-bottom: 12px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 8px;
}
.feedback-item:last-child {
  margin-bottom: 0;
}
.item-question {
  font-size: 12px;
  color: #333;
  margin-bottom: 6px;
  font-weight: 500;
  display: flex;
  // align-items: center;
}
.q-tag {
  display: inline-block;
  background: #00b852;
  color: #fff;
  font-size: 10px;
  padding: 0 4px;
  border-radius: 3px;
  margin-right: 6px;
  margin-top: 0;
  line-height: 16px;
  height: 16px;
}
.item-summary {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  // padding: 8px 10px;
  margin-left: 0;
  // background: #f8f8f8;
  border-radius: 4px;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 0;
  vertical-align: middle;
  height: 20px;

  .dot {
    width: 6px;
    height: 6px;
    margin: 0 2px;
    background-color: #999;
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.ai-loading-container {
  display: flex;
  align-items: center;
  padding: 10px 0;
}
.ai-avatar {
  min-width: 28px;
  width: 28px;
  height: 28px;
  background: #f0f2f5;
  color: #7d8592;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 14px;
}
.ai-bubble {
  background: #f2f3f5;
  padding: 10px 14px;
  border-radius: 0 12px 12px 12px;
  color: #333;
  font-size: 13px;
  display: flex;
  align-items: center;
  line-height: 1;
}

.follow-up-section {
  margin-top: 20px;
  border-top: 1px dashed #eee;
  padding-top: 15px;
}
.follow-up-header {
  font-size: 13px;
  color: #00b852;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.follow-up-header .hint-icon { // Use deep selector if scoped isn't applying to font-awesome properly or if needed
  margin-right: 6px;
  color: #00b852; // Lightbulb color
}

.follow-up-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.follow-up-btn {
  background: #fff;
  border: 1px solid #999; // Lighter shade of theme color
  color: #666;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  line-height: 1.4;
}

.follow-up-btn:active {
  background: #f0fff6;
}

.follow-up-btn .arrow-icon {
  color: #00b852;
  font-size: 12px;
  margin-left: 8px;
  flex-shrink: 0;
}

.text-measurer {
  visibility: hidden;
  position: absolute;
  white-space: pre;
  font-size: 12px;
  opacity: 0;
  z-index: -100;
}

.custom-input-wrapper {
  display: flex;
  align-items: center;
  position: relative; /* to contain absolute measurer potentially, though visibility hidden handles it */
}
.custom-question-input {
  /* width removed to be dynamic via style */
  background: #fff;
  border: 1px dashed #999;
  border-radius: 20px;
  padding: 0 10px;
  font-size: 12px;
  color: #333;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  line-height: normal;
  min-height: 24px;
}
.custom-question-input:focus {
  border-color: #00b852;
}
.send-btn {
  margin-left: 10px;
  color: #00b852;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  font-size: 16px;
}
</style>
